import React from 'react';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@cotera/client/v0/components/ui/tabs';
import type { AgentEditorTab } from './agent.model';
import { useAgentModel } from './agent.model';

const AGENT_EDITOR_TABS: ReadonlyArray<{
  value: AgentEditorTab;
  label: string;
}> = [
  { value: 'editor', label: 'Agent' },
  { value: 'chats', label: 'Chats' },
];

function isAgentEditorTab(value: string): value is AgentEditorTab {
  return value === 'editor' || value === 'chats';
}

/**
 * Which of the agent's two surfaces the pane below is showing: the prompt you write,
 * or the conversations it has had.
 *
 * A segmented control, the app's language for two surfaces in one pane — the dataset's
 * details and settings panels use it, so does the LLM tool panel, and so does the
 * comments rail's Open/Resolved.
 *
 * It used to be a pair of outline chips, matching the filter bands on the Library and
 * Automations pages. That was the wrong borrowing: those chips narrow one list, and
 * these swap between two different objects — an editor and a history — with nothing
 * being filtered. Two bordered chips also carried the same visual weight as the model
 * selector beside them, which left the header a row of same-weight boxes and no
 * hierarchy. One fused track, quieter than a bordered control, sits behind the actions
 * where it belongs.
 */
export const AgentEditorTabs: React.FC = () => {
  const model = useAgentModel();
  const tab = useWatchableValue(model.editorTabWatchable);

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => {
        // Unlike the toggle group this replaced, Radix's tabs cannot deselect, so
        // there is no empty value to guard against — only the type to narrow.
        if (isAgentEditorTab(value)) {
          model.setEditorTab(value);
        }
      }}
      className="w-max shrink-0"
    >
      <TabsList aria-label="Agent view">
        {AGENT_EDITOR_TABS.map(({ value, label }) => (
          <TabsTrigger key={value} value={value} className="px-3">
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
